/**
 * Shared helpers for tag-category M2M handling.
 *
 * The settings field lists mirror the backend's
 * demo/lib/tag_category_m2m.py (M2M_SETTINGS_INT_FIELDS / M2M_SETTINGS_BOOL_FIELDS)
 * — keep the two in sync, except `departure_key` (OPERATOR_DEFAULT_ONLY_BOOL_SETTINGS).
 */

export const TAG_CATEGORY_INT_SETTINGS = ['min_tags', 'max_tags'];

export const TAG_CATEGORY_BOOL_SETTINGS = [
  'user_selected',
  'chat_conversation',
  'visible_in_chat',
  'location_sharing',
  'location_all_if_no_tag',
  'user_association',
  'guides_from_tags',
  'copy_tags_to_guides',
  'copy_tags_to_subbookings',
  'remove_upon_date_changes',
  'checkin',
  'boarding',
  'visible_on_bookingmgmt',
  'cleanup_on_assignment',
  'departure_wide',
  'remove_guides_on_cleanup',
  'checkin_display',
  'remove_unreferenced_guides',
];

// On the operator's default rows only: Tour_Operator_Default_Tag_Category_M2MSerializer sends
// `departure_key`, no tour serializer does. It is therefore not in the trip-vs-default identity
// check above (a trip row would always read false), and only Trip Defaults shows it.
export const OPERATOR_DEFAULT_ONLY_BOOL_SETTINGS = ['departure_key'];

// The model's verbose_name and help_text (backend demo/models.py, Group_Tour_Tag_Category_M2M).
export const DEPARTURE_KEY_LABEL = 'Departure identity';
export const DEPARTURE_KEY_HELP =
  'Tags in this category identify the DEPARTURE INSTANCE. Participants sharing a tour and start time ' +
  'but carrying different tags from this category are different departures, and departure-wide ' +
  'assignment, guide cleanup and guide participants are kept separate between them.';

// Matches backend tag_category_m2m._should_use_operator_defaults.
// `tourOperator.use_operator_defaults` is the backend-resolved operator-level flag
// (Tour_OperatorSerializer, incl. franchise parent fallback); the raw config key is
// deliberately NOT in EXPOSED_CONFIG_SETTINGS, so `config.use_operator_defaults` is
// always undefined and remains only as a legacy fallback. Reading only the config
// path made every inherited operator default look like an editable trip-level row
// (CP-7983).
export function tourUsesOperatorDefaults(trip, tourOperator) {
  return !!trip?.use_operator_defaults || operatorAppliesDefaultTagCategories(tourOperator);
}

// The operator-wide half of the above (ops console › Trip Defaults): the default tag
// categories apply to every trip of the operator, whatever the trip's own switch says.
export function operatorAppliesDefaultTagCategories(tourOperator) {
  return !!(tourOperator?.use_operator_defaults || tourOperator?.config?.use_operator_defaults);
}

// Settings equality over the shared field lists. `sequence` is presentational
// and deliberately not compared.
export function tagCategorySettingsEqual(a, b) {
  return (
    TAG_CATEGORY_INT_SETTINGS.every((f) => Number(a?.[f] || 0) === Number(b?.[f] || 0)) &&
    TAG_CATEGORY_BOOL_SETTINGS.every((f) => !!a?.[f] === !!b?.[f])
  );
}

// Returns the operator default covering the same category with identical
// settings, or undefined.
export function findMatchingOperatorDefault(tagCategory, operatorDefaults) {
  const catId = tagCategory?.category?.id?.toString();
  if (!catId) return undefined;
  return (operatorDefaults || []).find(
    (d) => d?.category?.id?.toString() === catId && tagCategorySettingsEqual(tagCategory, d)
  );
}

// CP-7968: the trip's own tag category rows against the operator defaults, one entry per
// category, sorted by name. `status`:
//   'same'          the trip row has the default's settings: it wins over the default today,
//                   and keeps winning when the default changes;
//   'differs'       the trip row overrides the default;
//   'trip_only'     no default for the category;
//   'default_only'  no trip row: the default applies while the trip uses the defaults.
// `tripRows` may be the trip's effective set: only rows the trip owns (numeric
// `group_tour`, as in the backend's get_effective_tag_categories) count as trip rows.
// Duplicates of a category keep the first row, like the editor's grid.
export function compareTagCategoriesWithDefaults(tripRows, operatorDefaults) {
  const byCategory = new Map();
  const entryFor = (row) => {
    const id = row?.category?.id;
    if (id == null) return null;
    const key = id.toString();
    if (!byCategory.has(key)) {
      byCategory.set(key, { key, category: row.category, trip: null, def: null });
    }
    return byCategory.get(key);
  };
  (tripRows || []).forEach((row) => {
    if (typeof row?.group_tour !== 'number') return;
    const entry = entryFor(row);
    if (entry && !entry.trip) entry.trip = row;
  });
  (operatorDefaults || []).forEach((row) => {
    const entry = entryFor(row);
    if (entry && !entry.def) entry.def = row;
  });
  return [...byCategory.values()]
    .map((entry) => {
      let status = 'default_only';
      if (entry.trip && entry.def) {
        status = tagCategorySettingsEqual(entry.trip, entry.def) ? 'same' : 'differs';
      } else if (entry.trip) {
        status = 'trip_only';
      }
      return { ...entry, status };
    })
    .sort((a, b) => (a.category?.name || '').toLowerCase().localeCompare((b.category?.name || '').toLowerCase()));
}

const isDepartureCategory = (tagCategory) => tagCategory?.category?.code === 'departure';

// Whether the trip will have the "departure" tag category in effect, i.e. whether the
// backend's Group_Tour.has_departures() will be true once it is saved. A non-bespoke trip
// for which it is true needs departure times: adding a user to it requires picking one.
//
// `trip.tag_categories` is already an effective set (the backend's for a saved trip, the
// template tour's in the create form), so the operator-level flag is reflected there. What
// it misses is a default the trip only inherits once its own "Use Tour Operator default
// settings" switch is turned on — the categories table shows that Departure row, but no
// departure times were asked for, and the trip was saved unable to take users (CP-7922).
export function tripHasDepartureCategory(trip, tourOperator) {
  if ((trip?.tag_categories || []).some(isDepartureCategory)) return true;
  return !!trip?.use_operator_defaults && (tourOperator?.default_tag_categories || []).some(isDepartureCategory);
}
